/**
 * Frontend mirror of the backend Checkins role hierarchy (User < Admin < SuperAdmin), used only to
 * decide which controls to show. The API is the real authority — see CheckinsRoles on the server.
 * Roles arrive as prefixed "checkins:{Role}" claim values; the prefix isolates apps.
 */

export const CHECKINS_APP_KEY = 'checkins';

export type CheckinsRole = 'User' | 'Admin' | 'SuperAdmin';

// Low -> high. A held role satisfies any required role at or below its rank.
const HIERARCHY: readonly CheckinsRole[] = ['User', 'Admin', 'SuperAdmin'];

/** The bare Checkins role names parsed from raw "role" claim values; other apps' roles are dropped. */
export function checkinsRolesFromClaims(roleClaimValues: readonly string[]): CheckinsRole[] {
  const prefix = `${CHECKINS_APP_KEY}:`;
  return roleClaimValues
    .filter(value => value.toLowerCase().startsWith(prefix))
    .map(value => value.slice(prefix.length))
    .filter((role): role is CheckinsRole => (HIERARCHY as readonly string[]).includes(role));
}

/** True when one of the held roles ranks at or above the required role. */
export function satisfiesCheckinsRole(held: readonly CheckinsRole[], required: CheckinsRole): boolean {
  const requiredRank = HIERARCHY.indexOf(required);
  return held.some(role => HIERARCHY.indexOf(role) >= requiredRank);
}
